import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import * as d3 from 'd3';
import moment from 'moment';
import { COMPETENCY_REPORT_FREQUENCY } from '@/utils/constants';
import { getDateRangeArray, getWeekRangeByMonth} from '@/utils/date';
import { ReportDataCoordinate } from '@/models/groups/report';

@Component({
    name: 'texas-line-chart',
})

export default class LineChart extends Vue {

    // ------------------------------------------------------------------
    // Properties
    @Prop()
    private dataList: any;

    @Prop({default: 'daily'})
    private frequency!: string;

    @Prop()
    private activeMonth!: string;

    @Prop()
    private activeYear!: string;

    private tooltipData?: any = null;

    private tooltipStyle?: any = null;

    private get startDate() {
        return moment([this.activeYear, parseInt(this.activeMonth, 10) - 1]).toDate();
    }

    private get endDate() {
        return (this.activeMonth === moment().format('MM')
                && this.activeYear === moment().format('YYYY'))
                    ? moment().toDate()
                    : moment(this.startDate).endOf('month').toDate();
    }

    private get frequenceyLabel() {
        const frequenceyLabel =  COMPETENCY_REPORT_FREQUENCY.find(
            (reportFrequency: any) => (this.frequency === reportFrequency.frequency) );
        return frequenceyLabel
            ? frequenceyLabel.label
            : COMPETENCY_REPORT_FREQUENCY.find((reportFrequency: any) => (reportFrequency.frequency === 'weekly') );
    }


    private get dailyFrequencyLabels() {
        return getDateRangeArray(
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'));
    }

    private get weeklyFrequencyLabels() {
        const tillCurrentWeek = this.activeMonth === moment().format('MM')
                                && this.activeYear === moment().format('YYYY');
        return getWeekRangeByMonth(this.activeMonth, this.activeYear, tillCurrentWeek);
    }

    private get isCurrentMonthActive() {
        return this.activeMonth === moment().format('MM') && this.activeYear === moment().format('YYYY');
    }

    @Watch('dataList', {deep: true})
    private async onChartDataChange(data: any) {
       this.drawLineChart(data);
    }

    // -----------------------------------------------------------------
    // Hooks
    private mounted() {
        this.drawLineChart(this.dataList);
    }

    // ------------------------------------------------------------------
    // Methods

    private async drawLineChart(dataList: any) {
        d3.select(this.$el).select('.line-chart-container').select('svg').remove();
        // set the dimensions and margins of the graph
        const margin = { top: 10, right: 30, bottom: 30, left: 60 };
        const width = this.$el.clientWidth - margin.left - margin.right;
        const height = 200 - margin.top - margin.bottom;
        // append the svg object to the body of the page

        const dataSet = this.parseChartData(JSON.parse(JSON.stringify(dataList))) || [];
        if (dataSet.length) {
                    const svg = d3.select(this.$el).select('.line-chart-container')
                    .append('svg')
                    .attr('width', width + margin.left + margin.right)
                    .attr('height', height + margin.top + margin.bottom + 30)
                    .append('g')
                    .attr('transform',
                        'translate(' + margin.left + ',' + margin.top + ')');

                    const xMinValue: any = d3.min(dataSet, (d: any) => d.pos);
                    const xMaxValue: any = xMinValue + (dataSet.length + 0.3 - 1);

                    const x: any = d3.scaleLinear()
                    .domain([xMinValue - .3 , xMaxValue])
                    .range([0, width]);

                    svg.append('g')
                    .attr('transform', 'translate(0,' + height + ')')
                    .call(d3.axisBottom(x).tickValues(dataSet.map(
                        (data) => data.pos)).tickPadding(5).tickFormat((d: any) => {
                        return `${dataSet[d - 1].label}`;
                    }))
                    .attr('class', 'axis-line')
                    .attr('stroke', '#fff');

                    const yMaxValue: any = d3.max(dataSet, (d: any) => d.masteredCompetencies);


                    const y: any = d3.scaleLinear()
                    .domain([0, yMaxValue <= 10 ? 10 : yMaxValue])
                    .range([height, 0]);

                    svg.append('g')
                    .call(d3.axisLeft(y).ticks(6).tickSize(0).tickPadding(5))
                    .attr('class', 'axis-line')
                    .attr('stroke', '#fff');

                // Add the line
                    svg.append('path')
                    .datum(dataSet)
                    .attr('fill', 'none')
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 1)
                    .attr('d', d3.line()
                        .x((d: any) => x(d.pos))
                        .y((d: any) => y(d.masteredCompetencies)),
                    );
                // Lines
                    svg.append('g').selectAll('myline')
                    .data(dataSet)
                    .enter()
                    .append('line')
                    .attr('x1', (d: any) => x(d.pos))
                    .attr('x2', (d: any) => x(d.pos))
                    .attr('y2', y(0))
                    .attr('y1', (d: any) => y(d.masteredCompetencies))
                    .on('mousemove', (d: any) => {
                        this.tooltipActive(d, x, y);
                    })
                    .on('mouseleave', (d: any) => {
                        this.tooltipData = null;
                    })
                    .attr('stroke', (d: any) => {
                        return this.frequency === 'daily' ?
                            d.label === moment().format('DD') && this.isCurrentMonthActive ?
                                '#fff' :
                                '#ffffff3d' :
                                '#ffffff3d';
                    })
                    .attr('stroke-width', this.frequency === 'daily' ? 10 : 60);
                // Add the points
                    svg
                    .append('g')
                    .selectAll('dot')
                    .data(dataSet)
                    .enter()
                    .append('circle')
                    .attr('cx', (d: any) => x(d.pos) as number)
                    .attr('cy', (d: any) => y(d.masteredCompetencies))
                    .attr('r', 3)
                    .attr('fill', '#2073bb')
                    .attr('stroke', '#fff')
                    .on('mousemove', (d: any) => {
                        this.tooltipActive(d, x, y);
                    })
                    .on('mouseleave', (d: any) => {
                        this.tooltipData = null;
                    });

                    svg
                .append('g')
                .attr('transform', 'translate(-490, 210) rotate(-90)')
                .append('text')
                .attr('class', 'placeholder')
                .attr('x', '50')
                .attr('y', '445')
                .text('Competencies');

                    svg
            .append('g')
            .attr('transform', 'translate(5, 45) rotate(0)')
            .append('text')
            .attr('class', 'placeholder')
            .attr('x', '0')
            .attr('y', height)
            .text(this.frequency === 'daily' ? 'Day' : 'Week');
        }


    }

    private tooltipActive(d: any, x: any, y: any) {
        this.tooltipStyle = {
            toolX: x(d.pos) + 45,
            toolY: y(d.masteredCompetencies) - 30,
        };
        this.tooltipData = d.masteredCompetencies;
    }

    private parseChartData(dataList: any[] = []) {
        const dataSet: any[] = [];
        const labelByFrequency = this.frequency === 'daily' ? this.dailyFrequencyLabels : this.weeklyFrequencyLabels;
        labelByFrequency.forEach((frequencyLabel: string | any, index: number) => {
            let coordinateData: any = dataList.find(
                (data: any) => (data.label === index + 1 || data.label === frequencyLabel.week));
            const label = this.frequency === 'daily' ? moment(frequencyLabel).format('DD') : `${moment(frequencyLabel.startDate).format('DD')} - ${moment(frequencyLabel.endDate).format('DD')}`;
            if (coordinateData) {
                // coordinateData.formattedLabel = formattedLabel;
                coordinateData.label = label;
                coordinateData.pos = index + 1;
                coordinateData.masteredCompetencies = Math.max(0, coordinateData.masteredCompetencies);
            } else {
                coordinateData = {
                    label,
                    pos: index + 1,
                    // formattedLabel,
                    completedCompetencies: 0,
                    inferredCompetencies: 0,
                    inprogressCompetencies: 0,
                    notstartedCompetencies: 0,
                    masteredCompetencies: 0,
                };
            }
            dataSet.push(coordinateData);
        });
        return dataSet;
    }

}
